import { useCallback } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { SettingsCards, SettingsStates } from '../components/SettingsCards';
import type { SettingsResponse } from '../dashboard/types';

/**
 * Onboarding "Sync settings" step (M14 QA #18).
 *
 * Renders the SAME sync-settings card set as the dashboard Settings view
 * (`components/SettingsCards`) — one config surface, two entry points. This
 * sits between Stripe Connect and the products step so the tenant-wide sync
 * toggles (deal mirroring, delta deals, auto-link / auto-create products) are
 * decided before products are mapped and before the import runs.
 *
 * The toggles persist immediately to D1 (via POST admin/settings), exactly as
 * they do in the dashboard — so the gate is in effect when the first webhook /
 * bootstrap batch runs. Pipeline *provisioning* (creating the HubSpot
 * pipelines) still happens at "Start Import" (StepImport → POST
 * onboarding/provision), keyed off the createDeals value persisted here.
 */
export function StepSettings({ onDone, onBack }: { onDone: () => void; onBack?: () => void; }) {
  const fetcher = useCallback(
    (signal: AbortSignal) => api.get<SettingsResponse>('admin/settings', { signal }),
    []
  );
  const { data, loading, error, refetch } = useAsync(fetcher, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-ink-900">Sync settings</h2>
        <p className="mt-1 text-sm text-ink-500">
          Choose how Stripe data flows into HubSpot. These take effect
          immediately and you can change them later from the dashboard Settings.
        </p>
      </div>

      {/* M22: Company-level aggregates depend on HubSpot's auto-association
          toggle. Surface it here so B2B tenants know to enable it before the
          import runs. This is informational only — the toggle itself lives in
          HubSpot (CRM Settings → Objects → Companies → Automation). */}
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        <p className="font-medium">Enable Company-level revenue rollups</p>
        <p className="mt-1 text-blue-800">
          For revenue (LTV, MRR) to roll up to HubSpot <strong>Company</strong>{' '}
          records, enable HubSpot&rsquo;s{' '}
          <em>&ldquo;Create and associate companies with contacts&rdquo;</em>{' '}
          setting (Settings &rarr; Objects &rarr; Companies &rarr; Automation).
          When off, revenue data stays on Contacts only.
        </p>
      </div>

      {(loading || error || !data) && (
        <SettingsStates loading={loading} error={error} onRetry={refetch} />
      )}

      {data && <SettingsCards settings={data} onChanged={refetch} context="onboarding" />}

      <div className="flex items-center justify-between gap-3 border-t border-ink-100 pt-4">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="text-sm font-medium text-ink-400 transition-colors hover:text-ink-700"
          >
            ← Back
          </button>
        ) : (
          <p className="text-xs text-ink-400">
            Defaults are safe for most setups — you can skip ahead and adjust later.
          </p>
        )}
        <button className="btn-primary" onClick={onDone}>
          Continue
        </button>
      </div>
    </div>
  );
}
